import { WidgetCommunicationService } from "@/services/integrations/widget/WidgetCommunicationService";
import type { LeadConversions } from "@/types/ui/branding";
import type { SummaryCaptureSource } from "@/types/voice/summary-capture";

/**
 * Tells the customer's page that the chat captured a lead, so widget.js /
 * widget-v2.js can push it to their data layer and fire their Google Ads and
 * Meta tags. It has to happen in the visitor's browser: that is how the ad
 * platforms link the lead to the ad click.
 *
 * The page hosting the genie sets its conversion settings once the genie
 * loads; every capture surface reports through useSummaryCapture. Reported
 * once per genie per browser session, so a corrected email or a second
 * surface never counts as a second lead. No personal details are sent: every
 * script on the customer's site can read these messages.
 */

const REPORTED_KEY_PREFIX = "hg_lead_reported:";

let settings: LeadConversions | null = null;
const reported = new Set<string>();

export const setLeadConversionSettings = (next: LeadConversions | null | undefined): void => {
  settings = next ?? null;
};

const claimReport = (agentId: string): boolean => {
  const key = `${REPORTED_KEY_PREFIX}${agentId}`;
  if (reported.has(key)) return false;
  reported.add(key);
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, "1");
  } catch {
    // Storage blocked — the in-memory set still stops repeats on this page.
  }
  return true;
};

export const reportLeadCaptured = ({
  agentId,
  conversationId,
  source,
}: {
  agentId: string;
  conversationId?: string | null;
  source: SummaryCaptureSource;
}): void => {
  if (!claimReport(agentId)) return;

  const enabled = settings?.enabled === true;
  const googleSendTo = settings?.googleSendTo?.trim();
  WidgetCommunicationService.messages.leadCaptured({
    agentId,
    ...(conversationId ? { conversationId } : {}),
    source,
    ...(enabled && googleSendTo ? { googleSendTo } : {}),
    ...(enabled && settings?.metaLead !== false ? { metaLead: true } : {}),
  });
};
